import { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAuth } from "@clerk/clerk-react";
import { PDFDocument } from "pdf-lib";
import { BACKEND_URL } from "@/constants/api";

/**
 * Shared hook that encapsulates all report form state management:
 * - Form setup (react-hook-form + zod)
 * - Data loading (from API or class defaults)
 * - Live PDF regeneration with field highlighting
 * - Delta auto-save: 2.5s debounce after last change (reportId only)
 * - beforeunload guard for unsaved changes
 * - PDF scroll target computation from template field positions
 * - Step navigation with manual completion tracking
 *
 * Config:
 *   formSchema         - Zod schema for react-hook-form
 *   defaultValues      - Default form values
 *   fillPDF            - async (values, highlightRect?) => objectURL
 *   templateUrl        - URL to the PDF template (Vite static asset)
 *   STEPS              - Array of { id, title } step definitions
 *   STEP_FIELDS        - { [stepId]: string[] } fields per step
 *   STEP_ANCHOR_FIELDS - { [stepId]: pdfFieldName } for scroll targeting
 *   SCHEMA_TO_PDF_FIELD - { schemaField: pdfFieldName }
 *   getPageIndex       - (schemaField: string) => number (0-based page index)
 *   getInitialPage     - (stepId: number) => number (1-based page number for initial scroll targets)
 *
 * Props (passed through from the Page component):
 *   reportId           - MongoDB report ID (null for free trial / defaults mode)
 *   defaultsGroupId    - Group ID when editing class defaults
 */
export function useReportForm(config, props) {
  const {
    formSchema,
    defaultValues,
    fillPDF,
    templateUrl,
    STEPS,
    STEP_FIELDS,
    STEP_ANCHOR_FIELDS,
    SCHEMA_TO_PDF_FIELD,
    getPageIndex,
    getInitialPage,
  } = config;

  const { reportId, defaultsGroupId, groupGrade } = props;

  const [currentStep, setCurrentStep] = useState(1);
  const [filledPDF, setFilledPDF] = useState(null);
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showPostDownloadModal, setShowPostDownloadModal] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const hasSavedOnce = useRef(false);
  const [scrollTargets, setScrollTargets] = useState(
    Object.fromEntries(STEPS.map((s) => [s.id, { page: getInitialPage(s.id), topFraction: 0 }]))
  );

  const changedFieldsRef = useRef(new Set());
  const saveDebounceRef = useRef(null);
  const regenDebounceRef = useRef(null);
  const fieldPositionsRef = useRef({});
  const lastChangedFieldRef = useRef(null);
  const formPaneRef = useRef(null);
  const mountedRef = useRef(true);
  const flushSaveRef = useRef(null);
  const { getToken } = useAuth();

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues,
  });

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // Load saved data + generate initial PDF
  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;
    if (reportId) {
      getToken().then((token) =>
        fetch(`${BACKEND_URL}api/reports/${reportId}`, {
          headers: { Authorization: `Bearer ${token}` },
          signal,
        })
          .then((r) => r.json())
          .then((json) => {
            if (!mountedRef.current) return;
            const base = json.data && Object.keys(json.data).length > 0 ? json.data : form.getValues();
            const values = (!base.grade && groupGrade) ? { ...base, grade: groupGrade } : base;
            form.reset(values);
            fillPDF(values).then((url) => { if (mountedRef.current) setFilledPDF(url); });
          })
          .catch((err) => {
            if (err.name === "AbortError") return;
            fillPDF(form.getValues()).then((url) => { if (mountedRef.current) setFilledPDF(url); });
          })
      );
    } else if (defaultsGroupId) {
      getToken().then((token) =>
        fetch(`${BACKEND_URL}api/report-groups/${defaultsGroupId}`, {
          headers: { Authorization: `Bearer ${token}` },
          signal,
        })
          .then((r) => r.json())
          .then((json) => {
            if (!mountedRef.current) return;
            const values = { ...form.getValues(), ...(json.reportDefaults || {}) };
            form.reset(values);
            fillPDF(values).then((url) => { if (mountedRef.current) setFilledPDF(url); });
          })
          .catch((err) => {
            if (err.name === "AbortError") return;
            fillPDF(form.getValues()).then((url) => { if (mountedRef.current) setFilledPDF(url); });
          })
      );
    } else {
      fillPDF(form.getValues()).then((url) => { if (mountedRef.current) setFilledPDF(url); });
    }
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Build scroll targets + field bounding boxes from PDF template
  useEffect(() => {
    async function buildTargets() {
      try {
        const bytes = await fetch(templateUrl).then((r) => r.arrayBuffer());
        const pdfDoc = await PDFDocument.load(bytes);
        const pdfForm = pdfDoc.getForm();
        const pages = pdfDoc.getPages();

        const updated = {};
        for (const [stepStr, fieldName] of Object.entries(STEP_ANCHOR_FIELDS)) {
          const step = Number(stepStr);
          const pageIdx = getInitialPage(step) - 1;
          try {
            const f = pdfForm.getField(fieldName);
            const widgets = f.acroField.getWidgets();
            if (!widgets.length) throw new Error("no widgets");
            const rect = widgets[0].getRectangle();
            const pageH = pages[pageIdx].getHeight();
            const topFromTop = Math.max(0, pageH - rect.y - rect.height - 40);
            updated[step] = { page: pageIdx + 1, topFraction: Math.min(0.95, topFromTop / pageH) };
          } catch {
            updated[step] = { page: pageIdx + 1, topFraction: 0 };
          }
        }
        if (mountedRef.current) setScrollTargets((prev) => ({ ...prev, ...updated }));

        const positions = {};
        for (const [schemaField, pdfFieldName] of Object.entries(SCHEMA_TO_PDF_FIELD)) {
          const pageIdx = getPageIndex(schemaField);
          try {
            const f = pdfForm.getField(pdfFieldName);
            const widgets = f.acroField.getWidgets();
            if (!widgets.length) continue;
            const rect = widgets[0].getRectangle();
            positions[schemaField] = { pageIndex: pageIdx, x: rect.x, y: rect.y, width: rect.width, height: rect.height };
          } catch { /* field not in template */ }
        }
        fieldPositionsRef.current = positions;
      } catch (e) {
        console.error("Failed to build PDF scroll targets:", e);
      }
    }
    buildTargets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Watch form changes: mark dirty, track changed fields, debounce PDF regen + save
  useEffect(() => {
    const subscription = form.watch((_, { name }) => {
      // name is undefined when form.reset() fires — that's a data load, not a user change
      if (!name) return;

      changedFieldsRef.current.add(name);
      lastChangedFieldRef.current = name;

      if (mountedRef.current) setIsDirty(true);

      // PDF regen (skip for _completedFields - no PDF field for it)
      if (name !== "_completedFields") {
        if (regenDebounceRef.current) clearTimeout(regenDebounceRef.current);
        if (mountedRef.current) setIsPdfGenerating(true);
        regenDebounceRef.current = setTimeout(async () => {
          const changedField = lastChangedFieldRef.current;
          const highlightRect = changedField !== "_completedFields"
            ? (fieldPositionsRef.current[changedField] ?? null)
            : null;
          const url = await fillPDF(form.getValues(), highlightRect);
          if (mountedRef.current) {
            setFilledPDF(url);
            setIsPdfGenerating(false);
          }
        }, 500);
      }

      // Auto-save debounce (reportId only; defaults mode has no auto-save)
      if (reportId) {
        if (saveDebounceRef.current) clearTimeout(saveDebounceRef.current);
        saveDebounceRef.current = setTimeout(() => {
          flushSaveRef.current?.();
        }, 2500);
      }
    });
    return () => {
      subscription.unsubscribe();
      if (regenDebounceRef.current) clearTimeout(regenDebounceRef.current);
      if (saveDebounceRef.current) clearTimeout(saveDebounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, reportId]);

  // Flush save (delta PUT - only changed fields)
  flushSaveRef.current = () => {
    if (!changedFieldsRef.current.size) return;
    if (!reportId) return;

    // Snapshot and clear atomically; any changes during the request go into the now-empty ref
    const snapshot = new Set(changedFieldsRef.current);
    changedFieldsRef.current = new Set();

    if (mountedRef.current) {
      setIsDirty(false);
      setIsSaving(true);
    }

    const allValues = form.getValues();
    const delta = {};
    for (const key of snapshot) {
      delta[key] = allValues[key];
    }

    getToken().then((token) =>
      fetch(`${BACKEND_URL}api/reports/${reportId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ data: delta }),
      }).then((res) => {
        if (mountedRef.current) {
          setIsSaving(false);
          if (!res.ok) {
            // Restore snapshot on failure so changes aren't lost
            for (const key of snapshot) changedFieldsRef.current.add(key);
            setIsDirty(true);
          } else {
            hasSavedOnce.current = true;
          }
        }
      }).catch(() => {
        if (mountedRef.current) {
          setIsSaving(false);
          for (const key of snapshot) changedFieldsRef.current.add(key);
          setIsDirty(true);
        }
      })
    );
  };

  // Flush on unmount
  useEffect(() => {
    return () => { flushSaveRef.current?.(); };
  }, []);

  // beforeunload guard
  useEffect(() => {
    const handler = (e) => {
      if (changedFieldsRef.current.size > 0 && reportId) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [reportId]);

  // Navigation handlers
  function handleNext() {
    flushSaveRef.current?.();
    setCurrentStep((s) => Math.min(s + 1, STEPS.length));
    formPaneRef.current?.scrollTo({ top: 0 });
  }

  function handlePrev() {
    flushSaveRef.current?.();
    setCurrentStep((s) => Math.max(s - 1, 1));
    formPaneRef.current?.scrollTo({ top: 0 });
  }

  function handleDownload() {
    flushSaveRef.current?.();
    if (!filledPDF) return;
    fillPDF(form.getValues(), null).then((cleanUrl) => {
      const { studentName, oen } = form.getValues();
      const fileName = [studentName, oen].filter(Boolean).join("_").replace(/\s+/g, " ").trim() || "report-card";
      const link = document.createElement("a");
      link.href = cleanUrl;
      link.setAttribute("download", `${fileName}.pdf`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      if (!reportId && !defaultsGroupId) {
        setShowPostDownloadModal(true);
      }
    });
  }

  function toggleManualComplete(stepId) {
    const stepFields = STEP_FIELDS[stepId];
    const current = new Set(form.getValues("_completedFields") || []);
    const isComplete = stepFields.every((f) => current.has(f));
    if (isComplete) {
      stepFields.forEach((f) => current.delete(f));
    } else {
      stepFields.forEach((f) => current.add(f));
    }
    form.setValue("_completedFields", [...current]);
  }

  // Step completion tracking
  const watchedValues = form.watch();
  const completedFields = new Set(watchedValues._completedFields || []);
  const completedSteps = new Set(
    STEPS.filter((step) =>
      STEP_FIELDS[step.id].every((f) => completedFields.has(f))
    ).map((step) => step.id)
  );

  return {
    form,
    currentStep,
    setCurrentStep,
    filledPDF,
    isPdfGenerating,
    showPreview,
    setShowPreview,
    showPostDownloadModal,
    setShowPostDownloadModal,
    isDirty,
    isSaving,
    hasSavedOnce,
    scrollTargets,
    completedSteps,
    formPaneRef,
    handleNext,
    handlePrev,
    handleDownload,
    toggleManualComplete,
    flushSaveRef,
  };
}
